import { ENDPOINTS } from '@/util'
import { OperationSchema, QraftContext, RequestFnPayload, requestFn } from '@openapi-qraft/react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { createAPIClient } from '@/api'
import { components } from '@/api/schema'
import { useSessionStorage } from '@/hooks'
import { isTokenExpired, isTokenNearExpiry } from '@/hooks/useTokenValidator'

export const createRestClient = () => createAPIClient().rest
export type RestSchemas = components['schemas']

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
  },
})

const getReqFn = (token: string) => {
  const reqFnWithToken: <T>(schema: OperationSchema, requestInfo: RequestFnPayload) => Promise<T> = async (
    schema,
    requestInfo
  ) => {
    if (!token) {
      console.warn('No token found, please login again')
      window.location.replace('/')
      return Promise.reject(new Error('No token found'))
    }
    if (isTokenExpired(token)) {
      console.warn('Token expired, please login again')
      sessionStorage.clear()
      window.location.replace('/?authStatus=logout-success')
      return Promise.reject(new Error('Token expired'))
    }
    if (isTokenNearExpiry(token)) {
      const refreshToken = async () => {
        try {
          const response = await fetch('/rest/auth/refreshToken', {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
            },
          })

          if (response.ok) {
            const data = await response.json()
            sessionStorage.setItem('token', JSON.stringify(data.token))
            console.info('Token refreshed successfully.')
          } else {
            console.error('Failed to refresh token')
          }
        } catch {
          console.error('Failed to refresh token')
        }
      }
      await refreshToken()
    }
    const headers = token
      ? {
          Authorization: `Bearer ${token}`,
        }
      : {}
    return requestFn(schema, { ...requestInfo, headers })
  }
  return reqFnWithToken
}

export const QueryProvider = ({ children }: { children: React.ReactNode }) => {
  return (
    <QueryClientProvider client={queryClient}>
      <QraftContextProvider>{children}</QraftContextProvider>
      <ReactQueryDevtools />
    </QueryClientProvider>
  )
}

const QraftContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [token] = useSessionStorage('token', '')

  return (
    <QraftContext.Provider
      value={{
        baseUrl: ENDPOINTS.DOTNET,
        requestFn: token ? getReqFn(token) : requestFn,
        queryClient,
      }}
    >
      {children}
    </QraftContext.Provider>
  )
}
